/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Form } from '@mairistem/composants';
import {
  Section,
  SectionHeader,
  EtatCivil,
  TypeComponent,
} from '@mairistem/administres-utils';

import { DATE_FORMAT, OPTIONS_SEXE_ETCV_AVEC_INDETERMINE } from '@/constants';

import {
  ChampAdresse,
  ChampLieu,
  ChampProfession,
  ChampsEtatMatrimonialAnterieur,
  STYLE_TITRE_SECTION,
} from '@/components/Common';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit. Sans lui, `dayjs(valeur, DATE_FORMAT)` rend « Invalid Date » —
// silencieusement à l'écran, et de façon reproductible en test isolé.
dayjs.extend(customParseFormat);

interface InformationsConjointProps {
  /** Numéro du conjoint (1 ou 2) : rang, et clé des champs `conjoint{n}.*`. */
  numeroConjoint: 1 | 2;
  /**
   * RACINE de `data`, et non le conjoint seul.
   *
   * ⚠️ `ChampLieu` et `ChampAdresse` lisent leur valeur par `_.get(data, name)`,
   * avec un `name` qui porte le chemin complet (`conjoint1.adresseNaissance`) :
   * leur passer l'objet déjà cadré leur ferait chercher
   * `conjoint.conjoint1.adresseNaissance`, donc rien. L'écriture, elle,
   * aboutissait — la commune se retrouvait en base sans jamais s'afficher.
   *
   * Le conjoint en est dérivé pour les composants qui attendent l'objet cadré :
   * l'état civil de la lib et la situation antérieure.
   */
  data: Record<string, any>;
  /** Date du mariage, qui date la validité des communes saisies. */
  dateMariage?: string;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
  /** Variante d'affichage de l'état civil (cf. `constants/typeComponentByTypeActe`). */
  typeComponent?: TypeComponent;
}

/**
 * Bloc « INFORMATIONS SUR LE Nème CONJOINT » : état civil, lieu de naissance,
 * domicile, profession et situation matrimoniale antérieure.
 *
 * ⚠️ Deux choses à savoir :
 *
 * 1. Tous les champs sont écrits sous `conjoint{n}.` — d'où l'`attributePrefix`
 *    de l'`EtatCivil` de la lib et les `name` imbriqués des champs. Les ERREURS,
 *    elles, sont lues SANS préfixe : c'est la convention des validators, qui les
 *    regroupent par individu (cf. `validationStep2`). D'où les `cleErreur`.
 *
 *    ⚠️ Et la LECTURE dépend du composant : ceux qui prennent un `name` imbriqué
 *    veulent la RACINE de `data` (`ChampLieu`, `ChampAdresse`), les autres
 *    l'objet cadré. Les mélanger donne un champ qui enregistre sans jamais
 *    réafficher.
 * 2. La situation antérieure et l'ex-conjoint sont portés par l'ACTE
 *    (`am_ex_*` / `am_es_*`), pas par `ec_acte_individu`. Ils sont saisis ici
 *    parce que c'est du conjoint qu'il s'agit ; `buildActeMariagePayload` fait le
 *    partage.
 *
 * Les deux conjoints étant symétriques, un seul composant les sert.
 */
const InformationsConjoint = (props: InformationsConjointProps) => {
  const {
    numeroConjoint,
    data,
    dateMariage,
    errors = {},
    onChange,
    typeComponent = TypeComponent.NAISSANCE,
  } = props;

  const prefixe = `conjoint${numeroConjoint}.`;

  /** Le conjoint seul, pour les composants qui attendent l'objet cadré. */
  const conjoint = data?.[`conjoint${numeroConjoint}`] ?? {};

  /**
   * Validité des communes à la date du MARIAGE, et non à la date de naissance
   * du conjoint — que `ChampLieu` retiendrait par défaut, ses champs `*Naissance`
   * étant cherchés à plat.
   */
  const dateValidite = !_.isEmpty(dateMariage)
    ? dayjs(dateMariage, DATE_FORMAT).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  return (
    <Section
      title={`INFORMATIONS SUR LE ${numeroConjoint === 1 ? '1ER' : '2ÈME'} CONJOINT`}
      height="auto"
    >
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <EtatCivil
          title="État civil"
          typeComponent={typeComponent}
          optionsSexe={OPTIONS_SEXE_ETCV_AVEC_INDETERMINE}
          attributePrefix={prefixe}
          individu={conjoint}
          errors={errors}
          onChange={onChange}
          /*
           * Mariage posthume (AD-12884) : à la place de la case « Parent
           * décédé », sans date, comme dans ETCV.
           */
          libelleCaseDecede="Conjoint décédé"
          filiationInconnue={Boolean(conjoint?.decede)}
          onFiliationInconnueChange={(decede: boolean) =>
            onChange(null, { name: `${prefixe}decede`, value: decede })
          }
        />
        <ChampLieu
          data={data}
          errors={errors}
          onChange={onChange}
          name={`${prefixe}adresseNaissance`}
          cleErreur="adresseNaissance"
          dateValidite={dateValidite}
        />
        {/*
          Adresse COMPLÈTE (voie incluse) et non un simple lieu : le domicile
          porte une voie en base. Non obligatoire pour autant — la source est
          l'acte papier.
        */}
        <ChampAdresse
          data={data}
          errors={errors}
          onChange={onChange}
          name={`${prefixe}adresseDomicile`}
          cleErreur="adresseDomicile"
          title="Adresse de domicile"
          dateValidite={dateValidite}
        />
        {/* @ts-ignore */}
        <SectionHeader
          title="Divers"
          required={false}
          style={STYLE_TITRE_SECTION}
        />
        <div style={{ padding: '1em' }}>
          <Form.Group widths="equal">
            <ChampProfession
              name={`${prefixe}profession`}
              value={conjoint?.profession}
              errors={errors}
              onChange={onChange}
            />
          </Form.Group>
        </div>
        {/* @ts-ignore */}
        <SectionHeader
          title="État matrimonial antérieur"
          required={false}
          style={STYLE_TITRE_SECTION}
        />
        <ChampsEtatMatrimonialAnterieur
          data={conjoint}
          errors={errors}
          onChange={onChange}
          prefixe={prefixe}
        />
      </Form>
    </Section>
  );
};

export default InformationsConjoint;
